import {
  PencilSquareIcon,
  MagnifyingGlassIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  CloudArrowUpIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { BottomNavigationBar } from '../../components/BottomNavigationBar';
import { ContextBrowser, ExistingDocument } from '../../components/ContextBrowser/ContextBrowser';
import { ContextDocuments } from '../../components/ContextDocuments/ContextDocuments';
import { DocumentPreview } from '../../components/DocumentPreview/DocumentPreview';
import { UploadedFile } from '../../components/FileUploader/FileUploader';
import { PageHeader } from '../../components/PageHeader';
import { StageNavigationBar } from '../../components/StageNavigationBar/StageNavigationBar';
import { UploadModal } from '../../components/UploadModal';
import {
  STAGE_PAGE_CONTENT_WIDTH_CLASS,
  STAGE_PAGE_WIDTH_CLASS,
} from '../../constants/layout';
import {
  BACK_BUTTON_LABEL,
  CONTINUE_BUTTON_LABEL,
} from '../../constants/navigation';
import { useSearch } from '../../hooks/useSearch';
import { projectService } from '../../services/projectService';
import {
  ContextManagementState,
  UploadedDocument,
} from '../../services/projectStateService';
import { searchService } from '../../services/searchService';
import { 
  ProjectType, 
  ProjectItem, 
  ProjectStatus,
  ProjectContextResponse,
  ProjectContextUploadedDocumentType,
} from '../../types/project';
import { SearchFilters, SearchResultDocument } from '../../types/search';
import { isGuidelineBlobKey, toCanonicalDocumentKey } from '../../utils/documentKey';
import { logger } from '../../utils/logger';
import { getPreselectedKnowledgeBaseDocuments } from '../../utils/preselectedKnowledgeBase';
import {
  BUSINESS_REQUIREMENTS_DOCUMENT_REQUIRED_MESSAGE,
  BUSINESS_REQUIREMENTS_DOCUMENT_REQUIRED_TITLE,
  BUSINESS_REQUIREMENTS_DOCUMENT_REQUIRED_TOOLTIP,
  hasBusinessRequirementsDocument,
  hasCompletedBusinessRequirementsUpload,
} from '../../utils/projectContextRequirements';

// Upload/document status values shared across this screen. The explicit
// literal type annotations keep these usable as discriminated-union values
// (without them TypeScript widens the constants to `string`).
const DOC_STATUS_COMPLETED: 'completed' = 'completed';
const DOC_STATUS_ERROR: 'error' = 'error';
const DOC_STATUS_UPLOADING: 'uploading' = 'uploading';

// Project context item type discriminator and default document category.
const UPLOADED_DOCUMENT_TYPE = 'ProjectContextUploadedDocument';
const KNOWLEDGE_BASE_CATEGORY = 'Knowledge Base';

interface ContextManagementScreenProps {
  project: ProjectItem;
  onProjectUpdate: (_project: ProjectItem) => void;
  onNavigateToStage: (_stage: number) => void;
  // State management props
  contextState?: ContextManagementState;
  onUpdateContextState: (_state: Partial<ContextManagementState>) => void;
}

/**
 * Decode a reference-document identifier that may be a base64-encoded blob URL.
 * Knowledge base reference documents are persisted with a base64-encoded
 * file_url. Returns the decoded URL when the value is base64 of an http(s)
 * URL, otherwise returns the original value unchanged.
 * @param value - The raw file_url / identifier to decode
 * @return The decoded blob URL, or the original value when not encoded
 */
const decodeDocumentUrl = (value: string): string => {
  try {
    const decoded = atob(value);
    if (decoded.startsWith('http://') || decoded.startsWith('https://')) {
      return decoded;
    }
  } catch {
    // Not base64 - fall through and return the original value.
  }
  return value;
};

/**
 * Build a canonical key for a document so the same underlying file is treated
 * as a single context document regardless of whether its identifier is a raw
 * blob URL or a base64-encoded one.
 * @param doc - The document to derive a key for
 * @return A normalized key suitable for de-duplication
 */
const getCanonicalDocumentKey = (doc: ExistingDocument): string =>
  toCanonicalDocumentKey(doc.path || doc.id);

/**
 * Build a secondary de-duplication key from a document's file name so the same
 * underlying document is treated as a single context document even when its
 * id/path differ across sources (e.g. a knowledge-base blob path vs a search
 * index id for the same file). Guideline blobs are identified by their full
 * blob name instead, since different folders can hold files with the same name.
 * @param doc - The document to derive a name key for
 * @return A normalized file-name key, or empty string when unavailable
 */
const getDocumentNameKey = (doc: ExistingDocument): string =>
  isGuidelineBlobKey(getCanonicalDocumentKey(doc))
    ? ''
    : (doc.name ?? '').trim().toLowerCase();

/**
 * Remove duplicate documents that resolve to the same underlying file,
 * preserving the first occurrence.
 * @param docs - The documents to de-duplicate
 * @return A new array without duplicate documents
 */
const dedupeDocuments = (docs: ExistingDocument[]): ExistingDocument[] => {
  const seen = new Set<string>();
  const seenNames = new Set<string>();
  const result: ExistingDocument[] = [];
  for (const doc of docs) {
    const key = getCanonicalDocumentKey(doc);
    const nameKey = getDocumentNameKey(doc);
    if (seen.has(key) || (nameKey && seenNames.has(nameKey))) {
      continue;
    }
    seen.add(key);
    if (nameKey) {
      seenNames.add(nameKey);
    }
    result.push(doc);
  }
  return result;
};

type KnowledgeBaseMetadata = NonNullable<
  ContextManagementState['knowledgeBaseDocuments']
>[string];

/**
 * Build the stored metadata for a knowledge base document added to context.
 * @param doc - The document being added
 * @return Metadata keyed by file URL in the persisted context state
 */
const buildKnowledgeBaseMetadata = (doc: ExistingDocument): KnowledgeBaseMetadata => ({
  title: doc.name,
  summary: doc.summary,
  description: doc.description,
  category: doc.category,
  tags: doc.tags,
  author: doc.author,
  lastModified: doc.lastModified.toISOString(),
  source: doc.source,
  wiki_path: doc.wiki_path,
});

type SolutionContextFields = {
  interfaces: string;
  cloud: string;
  security: string;
  personas: string;
  database_technology: string;
  other: string;
};

type SolutionContextFieldName = keyof SolutionContextFields;

const EMPTY_SOLUTION_CONTEXT_FIELDS: SolutionContextFields = {
  interfaces: '',
  cloud: '',
  security: '',
  personas: '',
  database_technology: '',
  other: '',
};

const SOLUTION_IDEATION_FIELDS: Array<{
  key: SolutionContextFieldName;
  title: string;
  placeholder: string;
}> = [
  {
    key: 'interfaces',
    title: 'Business/Solution',
    placeholder: 'Describe business context and solution overview...',
  },
  {
    key: 'cloud',
    title: 'Data Architecture',
    placeholder: 'Describe data architecture and data flows...',
  },
  {
    key: 'security',
    title: 'Application Architecture',
    placeholder: 'Describe application architecture and components...',
  },
  {
    key: 'personas',
    title: 'Technical Architecture',
    placeholder: 'Describe technical architecture and infrastructure...',
  },
  {
    key: 'database_technology',
    title: 'Information Security Architecture',
    placeholder: 'Describe security architecture and requirements...',
  },
  {
    key: 'other',
    title: 'Whiteboarding/Additional Context',
    placeholder: 'Any other relevant context or whiteboarding notes...',
  },
];

/**
 * Convert SearchResultDocument to ExistingDocument for ContextBrowser
 * @param searchDoc - Search result document
 * @return ExistingDocument for ContextBrowser
 */
const convertSearchResultToExistingDocument = (searchDoc: SearchResultDocument): ExistingDocument => {
  return {
    id: searchDoc.id,
    name: searchDoc.name,
    type: searchDoc.type,
    category: searchDoc.category,
    size: searchDoc.size,
    lastModified: searchDoc.lastModified,
    author: searchDoc.author,
    description: searchDoc.description,
    tags: searchDoc.tags,
    path: searchDoc.path,
    wiki_path: searchDoc.wiki_path,
    relevanceScore: searchDoc.relevanceScore,
    summary: searchDoc.summary,
    snippet: searchDoc.snippet,
    source: searchDoc.source,
    chunks: searchDoc.chunks,
    searchMetadata: searchDoc.searchMetadata,
  };
};

/**
 * ContextManagementScreen - Upload and manage project context documents
 * Provides file upload, document browsing, and context quality analysis
 */
export const ContextManagementScreen: React.FC<ContextManagementScreenProps> = ({
  project,
  onProjectUpdate: _onProjectUpdate,
  onNavigateToStage,
  contextState,
  onUpdateContextState,
}) => {
  const navigate = useNavigate();
  const isBrownfield = (project.projectType ?? project.project_type) === ProjectType.BROWN_FIELD;
  // Search hook for knowledge base
  const { 
    searchResults, 
    isLoading: isSearchLoading, 
    manualSearch: performManualSearch,
    hasSearched,
  } = useSearch({
    debounceMs: 1000, // Increased debounce time
    minQueryLength: 3,
    autoSearch: false, // Disable auto-search
  });

  // State for tracking documents with summaries (Phase 2)
  const [documentsWithSummaries, setDocumentsWithSummaries] = useState<ExistingDocument[]>([]);
  // Convert UploadedDocument to UploadedFile for the FileUploader component
  const convertToUploadedFiles = (docs: UploadedDocument[]): UploadedFile[] => {
    return docs.map(doc => ({
      id: doc.id,
      file: new File([], doc.name, { type: doc.type }),
      name: doc.name,
      size: doc.size,
      type: doc.type,
      status: doc.status === DOC_STATUS_COMPLETED ? DOC_STATUS_COMPLETED : 
        doc.status === DOC_STATUS_ERROR ? DOC_STATUS_ERROR : DOC_STATUS_UPLOADING,
      progress: doc.status === DOC_STATUS_COMPLETED ? 100 : undefined,
      error: doc.status === DOC_STATUS_ERROR ? 'Upload failed' : undefined,
      uploadDate: doc.uploadDate,
      // Preserve metadata including blob_path
      metadata: doc.metadata,
    }));
  };

  // Local state for UI only (not persisted)
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>(() => 
    convertToUploadedFiles(contextState?.uploadedDocuments || []),
  );
  const [selectedDocuments, setSelectedDocuments] = useState<string[]>(() => 
    contextState?.selectedDocumentIds || [],
  );
  const [selectedSearchDocuments, setSelectedSearchDocuments] = useState<ExistingDocument[]>([]);
  const [previewDocument, setPreviewDocument] = useState<ExistingDocument | null>(null);
  const [projectInstructions, setProjectInstructions] = useState(
    contextState?.projectInstructions || '',
  );
  const [activeTab, setActiveTab] = useState<
    'upload' | 'wiki' | 'guidelines' | 'user_docs'
  >('upload');
  const [selectedDocumentType, setSelectedDocumentType] = useState<ProjectContextUploadedDocumentType>(() => {
    // Try to get from contextState first, then fall back to default
    if (contextState?.selectedDocumentType) {
      return contextState.selectedDocumentType as ProjectContextUploadedDocumentType;
    }
    return project.projectType === ProjectType.BROWN_FIELD 
      ? ProjectContextUploadedDocumentType.SAHLD
      : ProjectContextUploadedDocumentType.BUSINESS_REQUIREMENTS_DOCUMENT;
  });
  const [contextItems, setContextItems] = useState<ProjectContextResponse[]>([]);
  const [isSavingInstructions, setIsSavingInstructions] = useState(false);
  const [instructionsUpdatedAt, setInstructionsUpdatedAt] = useState<string | null>(null);
  const [solutionContext, setSolutionContext] = useState<SolutionContextFields>(
    EMPTY_SOLUTION_CONTEXT_FIELDS,
  );
  const [isSavingSolutionContext, setIsSavingSolutionContext] = useState(false);
  const [isGeneratingSummaries, setIsGeneratingSummaries] = useState(false);
  const [savedSolutionContextFields, setSavedSolutionContextFields] =
    useState<Set<SolutionContextFieldName>>(new Set());
  
  // Upload modal state
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({
    total: 0,
    completed: 0,
    failed: 0,
  });

  // Debounced save for instructions
  const saveInstructionsTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const saveSolutionContextTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Use documentsWithSummaries if available, otherwise fall back to immediate search results
  // This prevents the brief "no documents" flash
  const displayDocuments = useMemo(() => {
    if (documentsWithSummaries.length > 0) {
      return documentsWithSummaries;
    }
    // If we have search results but documentsWithSummaries is empty, use search results immediately
    if (searchResults.length > 0) {
      return searchResults.map(convertSearchResultToExistingDocument);
    }
    return [];
  }, [documentsWithSummaries, searchResults]);

  // Only count/show selections for documents that are actually in the current
  // results. Without this, an id selected in a previous search lingers and the
  // browser shows a misleading "1 selected" / "Add 1 document" even when the
  // new search returns nothing (and the stale id can never be added).
  const visibleSelectedDocuments = useMemo(() => {
    const visibleIds = new Set(displayDocuments.map(doc => doc.id));
    return selectedDocuments.filter(id => visibleIds.has(id));
  }, [displayDocuments, selectedDocuments]);

  const activeKnowledgeBaseSource = useMemo(() => {
    switch (activeTab) {
      case 'guidelines':
        return 'guidelines' as const;
      case 'user_docs':
        return 'user_docs' as const;
      case 'wiki':
      default:
        return 'wiki' as const;
    }
  }, [activeTab]);

  const knowledgeBaseSearchPlaceholder = useMemo(() => {
    switch (activeTab) {
      case 'guidelines':
        return 'Search guidelines and standards... (press Enter or click Search)';
      case 'user_docs':
        return 'Search user documents... (press Enter or click Search)';
      case 'wiki':
      default:
        return 'Search historical SAHLD / DSI documents... (press Enter or click Search)';
    }
  }, [activeTab]);

  // Update local state when contextState changes
  useEffect(() => {
    if (contextState) {
      setUploadedFiles(convertToUploadedFiles(contextState.uploadedDocuments || []));
      setSelectedDocuments(contextState.selectedDocumentIds || []);
      setProjectInstructions(contextState.projectInstructions || '');
      // Don't auto-populate selectedSearchDocuments from contextState - only show when explicitly added
    }
  }, [contextState]);

  // Load project context from API and sync with local state
  const preselectedPersistedForProjectRef = useRef<number | null>(null);
  const preselectedPersistingForProjectRef = useRef<number | null>(null);

  // Pre-selected guidelines are shown in the pane, so save them to the project too.
  const persistPreselectedGuidelines = async (documents: ExistingDocument[]) => {
    if (
      preselectedPersistedForProjectRef.current === project.id ||
      preselectedPersistingForProjectRef.current === project.id
    ) {
      return;
    }
    preselectedPersistingForProjectRef.current = project.id;

    try {
      const metadata: Record<string, KnowledgeBaseMetadata> = {};
      documents.forEach((doc) => {
        metadata[doc.path] = buildKnowledgeBaseMetadata(doc);
      });
      const savedReferences = await projectService.addMultipleReferenceDocuments(
        project.id,
        documents.map((doc) => doc.path),
      );
      const savedFileUrls = new Set(
        savedReferences
          .filter((reference) => reference.type === 'ProjectContextReferenceDocument')
          .map((reference) => reference.file_url),
      );
      const missingDocuments = documents
        .map((doc) => doc.path)
        .filter((fileUrl) => !savedFileUrls.has(fileUrl));
      if (missingDocuments.length > 0) {
        const retriedReferences =
          await projectService.addMultipleReferenceDocuments(
            project.id,
            missingDocuments,
          );
        retriedReferences
          .filter((reference) => reference.type === 'ProjectContextReferenceDocument')
          .forEach((reference) => savedFileUrls.add(reference.file_url));
      }
      const unresolvedDocuments = documents
        .map((doc) => doc.path)
        .filter((fileUrl) => !savedFileUrls.has(fileUrl));
      if (unresolvedDocuments.length > 0) {
        throw new Error(
          `Could not save pre-selected guidelines: ${unresolvedDocuments.join(', ')}`,
        );
      }
      preselectedPersistedForProjectRef.current = project.id;
      onUpdateContextState({
        knowledgeBaseDocuments: {
          ...(contextState?.knowledgeBaseDocuments ?? {}),
          ...metadata,
        },
        lastModified: new Date().toISOString(),
      });
      setContextItems(await projectService.getProjectContext(project.id));
    } catch (error) {
      logger.error('Failed to save pre-selected guidelines to the project:', error);
    } finally {
      preselectedPersistingForProjectRef.current = null;
    }
  };

  // Load project context from API and sync with local state
  useEffect(() => {
    const loadProjectContext = async () => {
      try {
        const context = await projectService.getProjectContext(project.id);
        setContextItems(context);
        
        // Sync uploaded documents with context state
        const uploadedDocsFromAPI = context
          .filter(item => item.type === UPLOADED_DOCUMENT_TYPE)
          .map(item => {
            if ('name' in item && 'blob_path' in item && 'document_type' in item) {
              const uploadedDoc: UploadedDocument = {
                id: item.blob_path, // Use blob_path as unique ID
                name: item.name,
                type: item.document_type as string, // Convert enum to string
                size: 0, // Size not available from API
                uploadDate: ('updated_at' in item && item.updated_at) ? item.updated_at : '',
                status: DOC_STATUS_COMPLETED,
                summary: `${item.document_type} document`,
                metadata: { blob_path: item.blob_path },
              };
              return uploadedDoc;
            }
            return null;
          })
          .filter((doc): doc is UploadedDocument => doc !== null);
        
        // Get reference documents (selected knowledge base items that were actually added to context)
        const refDocs = context
          .filter(item => item.type === 'ProjectContextReferenceDocument')
          .map(item => {
            if ('file_url' in item) {
              // Get stored metadata if available
              const storedMetadata = contextState?.knowledgeBaseDocuments?.[item.file_url];

              // The persisted file_url may be base64-encoded; decode it so the
              // display name and File Location show the real blob URL instead
              // of an unreadable encoded string.
              const decodedUrl = decodeDocumentUrl(item.file_url);

              // Create ExistingDocument from reference document for display in context pane
              const fileName = decodedUrl.split('/').pop() || 'Unknown Document';
              const existingDoc: ExistingDocument = {
                id: decodedUrl, // Use decoded URL as ID so duplicates collapse
                name: storedMetadata?.title || fileName,
                type: (storedMetadata?.type as string) || 'Other',
                category: storedMetadata?.category || KNOWLEDGE_BASE_CATEGORY,
                size: 0, // Size not available
                lastModified: storedMetadata?.lastModified 
                  ? new Date(storedMetadata.lastModified) 
                  : new Date(),
                author: storedMetadata?.author || 'Unknown',
                tags: storedMetadata?.tags || [],
                path: decodedUrl,
                wiki_path: storedMetadata?.wiki_path,
                description: storedMetadata?.description || 'Previously added knowledge base document',
                summary: storedMetadata?.summary || 'No summary available',
                source: storedMetadata?.source,
              };
              return existingDoc;
            }
            return null;
          })
          .filter((doc): doc is ExistingDocument => doc !== null);
        
        // Get instructions
        const instructionsItem = context.find(item => item.type === 'ProjectContextInstructions');
        const instructions = instructionsItem && 'instructions' in instructionsItem 
          ? instructionsItem.instructions 
          : '';
        const instructionsTimestamp = instructionsItem && 'updated_at' in instructionsItem 
          ? instructionsItem.updated_at 
          : null;
        
        
        // Update local state
        setUploadedFiles(convertToUploadedFiles(uploadedDocsFromAPI));
        setSelectedDocuments([]); // Reset search selections on load
        // Seed the selection with the user's pre-selected knowledge base docs
        // (configured during the welcome flow) when the project doesn't yet
        // have any reference documents of its own.
        if (refDocs.length === 0) {
          const preselected = getPreselectedKnowledgeBaseDocuments();
          if (preselected.length > 0) {
            const seeded: ExistingDocument[] = preselected.map((kbDoc) => ({
              id: kbDoc.id,
              name: kbDoc.name,
              type: KNOWLEDGE_BASE_CATEGORY,
              category: kbDoc.category ?? KNOWLEDGE_BASE_CATEGORY,
              size: 0,
              lastModified: kbDoc.last_modified
                ? new Date(kbDoc.last_modified)
                : new Date(),
              author: 'System',
              tags: kbDoc.keywords ?? [],
              path: kbDoc.path ?? kbDoc.url ?? kbDoc.id,
              description:
                kbDoc.description ?? 'Pre-selected from your knowledge base',
              summary: kbDoc.description ?? undefined,
              source: 'guidelines',
            }));
            const seededDocuments = dedupeDocuments(seeded);
            setSelectedSearchDocuments(seededDocuments);
            void persistPreselectedGuidelines(seededDocuments);
          } else {
            setSelectedSearchDocuments(dedupeDocuments(refDocs));
          }
        } else {
          // Load previously added knowledge base documents (de-duplicated)
          setSelectedSearchDocuments(dedupeDocuments(refDocs));
        }
        if (instructions) {
          setProjectInstructions(instructions);
        }
        setInstructionsUpdatedAt(instructionsTimestamp || null);

        try {
          const additionalFields = await projectService.loadAdditionalContextFields(
            project.id,
          );
          setSolutionContext(additionalFields);
          setSavedSolutionContextFields(
            new Set(
              Object.entries(additionalFields)
                .filter(([, value]) => value.trim())
                .map(([key]) => key as SolutionContextFieldName),
            ),
          );
        } catch (loadError) {
          // eslint-disable-next-line no-console
          console.error('Failed to load solution ideation fields:', loadError);
        }
        
        // Update persistent state
        onUpdateContextState({
          uploadedDocuments: uploadedDocsFromAPI,
          selectedDocumentIds: [], // Always reset search selections on load
          projectInstructions: instructions || contextState?.projectInstructions || '',
          lastModified: new Date().toISOString(),
        });
        
      } catch (error) {
        logger.error('Failed to load project context:', error);
      }
    };

    loadProjectContext();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project.id]); // Intentionally omitting onUpdateContextState to avoid infinite loop

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveInstructionsTimeoutRef.current) {
        clearTimeout(saveInstructionsTimeoutRef.current);
      }
      if (saveSolutionContextTimeoutRef.current) {
        clearTimeout(saveSolutionContextTimeoutRef.current);
      }
    };
  }, []);

  // Helper function to format UTC timestamp to local time
  const formatTimestampToLocal = (utcTimestamp: string): string => {
    try {
      // Ensure timestamp is treated as UTC by adding Z if missing
      const normalizedTimestamp = utcTimestamp.endsWith('Z') || 
        utcTimestamp.includes('+') || 
        utcTimestamp.includes('-', 10)
        ? utcTimestamp 
        : `${utcTimestamp}Z`;
      
      const date = new Date(normalizedTimestamp);
      
      // Format to local time with date and time
      return date.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
    } catch {
      return utcTimestamp; // Fallback to original string if parsing fails
    }
  };

  // Handle file upload with document type
  const handleFileUpload = useCallback(async (files: UploadedFile[]) => {
    // Open upload modal and initialize progress
    setIsUploadModalOpen(true);
    setUploadProgress({
      total: files.length,
      completed: 0,
      failed: 0,
    });
    
    // Add all files to state immediately with uploading status
    const newUploadingFiles = files.map(file => ({ ...file, status: DOC_STATUS_UPLOADING }));
    setUploadedFiles(prev => [...prev, ...newUploadingFiles]);
    
    // Keep track of uploaded file names for merging with API response
    // Some files (like PDFs) may still be processing on the backend
    const uploadedFileNames = new Set(files.map(f => f.name));
    
    // Track upload promises
    const uploadPromises = files.map(async (fileInfo, _index) => {
      try {
        // Upload to backend
        const uploadResponse = await projectService.uploadContextDocument(
          project.id,
          fileInfo.file,
          selectedDocumentType,
        );
        
        // Update progress
        setUploadProgress(prev => ({
          ...prev,
          completed: prev.completed + 1,
        }));
        
        // Mark this file as completed and include metadata from the upload response
        return { 
          id: fileInfo.id, 
          status: DOC_STATUS_COMPLETED,
          metadata: uploadResponse.type === UPLOADED_DOCUMENT_TYPE ? {
            blob_path: uploadResponse.blob_path,
            document_type: uploadResponse.document_type,
          } : undefined,
        };
      } catch (error) {
        // Handle upload error and update failed count
        logger.error('File upload failed:', fileInfo.name, error);
        setUploadProgress(prev => ({
          ...prev,
          failed: prev.failed + 1,
        }));
        
        // Mark this file as error
        return { id: fileInfo.id, status: DOC_STATUS_ERROR, error: 'Upload failed. Please try again.' };
      }
    });
    
    // Wait for all uploads to complete
    const results = await Promise.all(uploadPromises);
    
    // Update all file statuses at once
    setUploadedFiles(prev => prev.map(f => {
      const result = results.find(r => r.id === f.id);
      if (result) {
        return { 
          ...f, 
          status: result.status,
          progress: result.status === DOC_STATUS_COMPLETED ? 100 : undefined,
          error: 'error' in result ? result.error : undefined,
          metadata: 'metadata' in result ? result.metadata : f.metadata,
        };
      }
      return f;
    }));
    
    // After all uploads, refresh the entire context from API to get the real state
    // PDFs and other complex documents may still be processing, so we need to merge
    // local state with API state for consistency
    try {
      let updatedContext = await projectService.getProjectContext(project.id);

      // For binary files (PDF, Excel, PPT), they may still be processing
      // For meeting transcripts, the backend converts .docx to .txt after summarization
      // Use longer polling for meeting transcripts due to AI summarization time
      const isMeetingTranscript = selectedDocumentType === 'meeting_transcript';
      const maxRetries = isMeetingTranscript ? 15 : 3; // Up to 30 seconds for transcripts
      const initialDelayMs = isMeetingTranscript ? 2000 : 200; // 2s intervals for transcripts
      let retries = 0;
      const uploadedFileNamesArray = Array.from(uploadedFileNames);

      while (retries < maxRetries) {
        const apiFileNames = new Set(
          updatedContext
            .filter(item => item.type === UPLOADED_DOCUMENT_TYPE)
            .map(item => ('name' in item ? item.name : null))
            .filter(
              (name): name is string => name !== null,
            ),
        );

        // Helper function to extract base name (without extension)
        const getBaseName = (filename: string) => {
          return filename.substring(0, filename.lastIndexOf('.'));
        };

        // Check if all uploaded files are in API response
        // For meeting transcripts, match by checking if the summary file appears
        // since backend converts .docx to _summary.txt after summarization
        const allFilesPresent = uploadedFileNamesArray.every(name => {
          const isMeetingTranscript = selectedDocumentType === 'meeting_transcript';
          if (isMeetingTranscript) {
            // For meeting transcripts, check if a summary file with matching base name appears
            // The summary file pattern is: {original_name}_summary.txt
            const uploadedBaseName = getBaseName(name);
            const expectedSummaryName = `${uploadedBaseName}_summary.txt`;
            
            const matchingItem = updatedContext.find(item => 
              item.type === UPLOADED_DOCUMENT_TYPE &&
              'name' in item &&
              item.name === expectedSummaryName,
            );
            // File appears in context only when summary is ready
            return matchingItem !== undefined;
          }
          return apiFileNames.has(name);
        });

        if (allFilesPresent || retries === maxRetries - 1) {
          break;
        }

        // Use appropriate delay strategy based on document type
        const delayMs = isMeetingTranscript 
          ? initialDelayMs  // Fixed 2s intervals for meeting transcripts
          : initialDelayMs * Math.pow(2, retries); // Exponential backoff for others
        await new Promise(resolve => setTimeout(resolve, delayMs));
        retries++;
        updatedContext = await projectService.getProjectContext(
          project.id,
        );
      }
      
      setContextItems(updatedContext);
      
      // Update the uploaded files state with real data from API
      const uploadedDocsFromAPI = updatedContext
        .filter(item => item.type === UPLOADED_DOCUMENT_TYPE)
        .map(item => {
          if ('name' in item && 'blob_path' in item && 'document_type' in item) {
            const uploadedDoc: UploadedDocument = {
              id: item.blob_path,
              name: item.name,
              type: item.document_type as string,
              size: 0,
              uploadDate: ('updated_at' in item && item.updated_at) ? item.updated_at : '',
              status: DOC_STATUS_COMPLETED,
              summary: `${item.document_type} document`,
              metadata: { blob_path: item.blob_path },
            };
            return uploadedDoc;
          }
          return null;
        })
        .filter((doc): doc is UploadedDocument => doc !== null);
      
      // Create a map of files from API by name for quick lookup
      const apiFilesByName = new Map(uploadedDocsFromAPI.map(doc => [doc.name, doc]));
      
      // Merge: Keep ALL files in uploadedFiles, updating with API data where available
      // Use functional state update to get the most current state
      setUploadedFiles(currentFiles => {
        const mergedFiles = currentFiles
          .map(localFile => {
            // Check if this file matches one we just uploaded
            const wasJustUploaded = uploadedFileNames.has(localFile.name);
            
            // Check if file exists in API response
            const apiFile = apiFilesByName.get(localFile.name);
            
            if (wasJustUploaded) {
              // For newly uploaded files, check if it's now in the API
              if (apiFile) {
                // File appeared in API - use the authoritative API version
                return convertToUploadedFiles([apiFile])[0];
              }
              // File not in API yet but upload completed - keep local version as-is
              // (whether it's 'completed', 'error', or 'uploading')
              return localFile;
            }
            
            // For files that weren't just uploaded, update from API if available
            // but preserve local version if not in API
            if (apiFile) {
              return convertToUploadedFiles([apiFile])[0];
            }
            return localFile;
          });
        
        // Log merged files for debugging
        
        // Convert merged files to UploadedDocument format for persistent state
        const mergedDocuments: UploadedDocument[] = mergedFiles.map(file => {
          // Determine file status
          let fileStatus: 'completed' | 'error' | 'processing' = 'processing';
          if (file.status === DOC_STATUS_COMPLETED) {
            fileStatus = DOC_STATUS_COMPLETED;
          } else if (file.status === DOC_STATUS_ERROR) {
            fileStatus = 'error';
          }
          
          return {
            id: file.id,
            name: file.name,
            type: file.type,
            size: file.size,
            uploadDate: file.uploadDate ?? '',
            status: fileStatus,
            summary: file.status === DOC_STATUS_COMPLETED ? `${file.type} document` : undefined,
            metadata: file.metadata ?? {},
          };
        });
        
        // Update persistent state in next tick to avoid setState during render
        setTimeout(() => {
          onUpdateContextState({
            uploadedDocuments: mergedDocuments,
            lastModified: new Date().toISOString(),
          });
        }, 0);
        
        return mergedFiles;
      });
    } catch (refreshError) {
      logger.error('Failed to refresh context after upload:', refreshError);
    }
    
    // Close modal after a brief delay to show completion status
    const UPLOAD_MODAL_CLOSE_DELAY_MS = 2000;
    setTimeout(() => {
      setIsUploadModalOpen(false);
    }, UPLOAD_MODAL_CLOSE_DELAY_MS);
  }, [project.id, selectedDocumentType, onUpdateContextState]);

  // File upload handlers with state persistence
  const handleFilesUploaded = useCallback((newFiles: UploadedFile[]) => {
    handleFileUpload(newFiles);
  }, [handleFileUpload]);

  const handleFileRemoved = useCallback(async (fileId: string) => {
    try {
      // Find the file to get its blob path
      const fileToRemove = uploadedFiles.find(f => f.id === fileId);
      if (fileToRemove) {
        // Find corresponding context item to get blob path
        const contextItem = contextItems.find(item => 
          item.type === UPLOADED_DOCUMENT_TYPE && 
          'name' in item && 
          item.name === fileToRemove.name,
        );
        
        if (contextItem && 'blob_path' in contextItem) {
          await projectService.deleteUploadedContextDocument(project.id, contextItem.blob_path);
          
          // Refresh context items
          const updatedContext = await projectService.getProjectContext(project.id);
          setContextItems(updatedContext);
        }
      }
      
      // Update UI state
      const updatedFiles = uploadedFiles.filter(file => file.id !== fileId);
      setUploadedFiles(updatedFiles);
      
      // Remove from persistent state
      onUpdateContextState({
        uploadedDocuments: (contextState?.uploadedDocuments || []).filter(doc => doc.id !== fileId),
        lastModified: new Date().toISOString(),
      });
    } catch (error) {
      logger.error('Failed to update context state:', error);
    }
  }, [uploadedFiles, contextItems, project.id, contextState, onUpdateContextState]);

  // Document selection handlers with state persistence
  const handleDocumentSelect = useCallback((documentId: string) => {
    const updatedSelection = [...selectedDocuments, documentId];
    setSelectedDocuments(updatedSelection);
    
    // NOTE: Do NOT add to selectedSearchDocuments here - only add when user clicks "Add Selected"
    // Documents should only appear in context pane after explicit addition via handleAddSelectedDocuments
    
    // Save selection to persistent state (for UI state only, not context documents)
    onUpdateContextState({
      selectedDocumentIds: updatedSelection,
      lastModified: new Date().toISOString(),
    });
  }, [selectedDocuments, onUpdateContextState]);

  const handleDocumentDeselect = useCallback((documentId: string) => {
    const updatedSelection = selectedDocuments.filter(id => id !== documentId);
    setSelectedDocuments(updatedSelection);
    
    // NOTE: Do NOT remove from selectedSearchDocuments here - that should only happen 
    // when user explicitly removes from context via handleSearchDocumentRemove
    
    // Save selection to persistent state (for UI state only, not context documents)
    onUpdateContextState({
      selectedDocumentIds: updatedSelection,
      lastModified: new Date().toISOString(),
    });
  }, [selectedDocuments, onUpdateContextState]);

  // Handler for removing search documents from context view
  const handleSearchDocumentRemove = useCallback(async (documentId: string) => {
    try {
      // Find the document to remove from selectedSearchDocuments
      const docToRemove = selectedSearchDocuments.find(doc => doc.id === documentId);
      if (docToRemove) {
        // Remove from backend by finding the file URL
        await projectService.deleteReferenceDocument(project.id, docToRemove.path);
        
        // Refresh context items
        const updatedContext = await projectService.getProjectContext(project.id);
        setContextItems(updatedContext);
      }
      
      // Update local state
      const updatedSelection = selectedDocuments.filter(id => id !== documentId);
      setSelectedDocuments(updatedSelection);
      setSelectedSearchDocuments(prev => prev.filter(doc => doc.id !== documentId));
      
      // Save to persistent state
      onUpdateContextState({
        selectedDocumentIds: updatedSelection,
        lastModified: new Date().toISOString(),
      });
    } catch (error) {
      logger.error('Failed to remove search document:', error);
    }
  }, [selectedSearchDocuments, selectedDocuments, project.id, onUpdateContextState]);

  // Update project instructions with state persistence and API save
  const handleInstructionsChange = useCallback(async (value: string) => {
    setProjectInstructions(value);
    
    // Save to persistent state immediately (local state only)
    onUpdateContextState({
      projectInstructions: value,
      lastModified: new Date().toISOString(),
    });

    // Clear existing timeout if any
    if (saveInstructionsTimeoutRef.current) {
      clearTimeout(saveInstructionsTimeoutRef.current);
    }

    // Debounced save to API (2 second delay)
    saveInstructionsTimeoutRef.current = setTimeout(async () => {
      if (value.trim()) {
        try {
          setIsSavingInstructions(true);
          const response = await projectService.saveInstructions(project.id, value);
          
          
          // Refresh context items to get updated data
          const updatedContext = await projectService.getProjectContext(project.id);
          setContextItems(updatedContext);
          
          
          // Update timestamp from response
          const instructionsItem = updatedContext.find(item => item.type === 'ProjectContextInstructions');
          
          if (instructionsItem && 'updated_at' in instructionsItem && instructionsItem.updated_at) {
            setInstructionsUpdatedAt(instructionsItem.updated_at);
          }
        } catch (error) {
          logger.error('Failed to save instructions:', error);
        } finally {
          setIsSavingInstructions(false);
        }
      }
    }, 2000);
  }, [project.id, onUpdateContextState]);

  /** Save the latest Solution Ideation fields after the user pauses editing. */
  const saveSolutionContextFields = useCallback((fields: SolutionContextFields) => {
    if (saveSolutionContextTimeoutRef.current) {
      clearTimeout(saveSolutionContextTimeoutRef.current);
    }

    saveSolutionContextTimeoutRef.current = setTimeout(async () => {
      const fieldsToSave: SolutionContextFields = {
        interfaces: fields.interfaces.trim(),
        cloud: fields.cloud.trim(),
        security: fields.security.trim(),
        personas: fields.personas.trim(),
        database_technology: fields.database_technology.trim(),
        other: fields.other.trim(),
      };
      const savedFieldNames = Object.entries(fieldsToSave)
        .filter(([, value]) => value.length > 0)
        .map(([key]) => key as SolutionContextFieldName);

      try {
        setIsSavingSolutionContext(true);
        await projectService.saveAdditionalContextFields(project.id, fieldsToSave);
        setSavedSolutionContextFields(new Set(savedFieldNames));
      } catch (saveError) {
        // eslint-disable-next-line no-console
        console.error('Failed to save solution ideation fields:', saveError);
      } finally {
        setIsSavingSolutionContext(false);
      }
    }, 2000);
  }, [project.id]);

  /** Update and persist an individual Solution Ideation field. */
  const handleSolutionContextChange = (
    fieldName: SolutionContextFieldName,
    value: string,
  ) => {
    const updatedFields = { ...solutionContext, [fieldName]: value };
    setSolutionContext(updatedFields);
    setSavedSolutionContextFields((previous) => {
      const next = new Set(previous);
      next.delete(fieldName);
      return next;
    });
    saveSolutionContextFields(updatedFields);
  };

  /** Generate and persist Solution Ideation summaries from the project context. */
  const handleGenerateSummaries = async () => {
    setIsGeneratingSummaries(true);
    try {
      const summaries = await projectService.getContextSummaries(project.id);
      const generatedFields: SolutionContextFields = {
        interfaces: summaries.business_solution,
        cloud: summaries.data_architecture,
        security: summaries.application_architecture,
        personas: summaries.technical_architecture,
        database_technology: summaries.information_security_architecture,
        other: summaries.whiteboarding_additional_context,
      };
      setSolutionContext(generatedFields);
      saveSolutionContextFields(generatedFields);
    } catch (generationError) {
      // eslint-disable-next-line no-console
      console.error('Failed to generate solution ideation summaries:', generationError);
    } finally {
      setIsGeneratingSummaries(false);
    }
  };

  // Handle adding selected documents from search results to the project context
  const handleAddSelectedDocuments = useCallback((selectedDocumentIds: string[]) => {
    // Run async operation without blocking
    (async () => {
      try {
        // Get the documents that correspond to the selected IDs from display documents
        const selectedDocs = displayDocuments.filter(doc =>
          selectedDocumentIds.includes(doc.id),
        );

        // Skip documents already present in the context so the same file is
        // never persisted twice. Compare both canonical keys (ids/paths may
        // differ) and file names (same doc from different sources).
        const existingKeys = new Set(
          selectedSearchDocuments.map(getCanonicalDocumentKey),
        );
        const existingNames = new Set(
          selectedSearchDocuments
            .map(getDocumentNameKey)
            .filter((name) => name.length > 0),
        );
        const documentsToAdd = dedupeDocuments(selectedDocs).filter(
          doc =>
            !existingKeys.has(getCanonicalDocumentKey(doc)) &&
            !existingNames.has(getDocumentNameKey(doc)),
        );

        // Convert documents to file URLs (using their path property)
        const fileUrls = documentsToAdd.map(doc => doc.path);
        
        if (fileUrls.length > 0) {
          // Create metadata map for the knowledge base documents
          // Use the file URL as the key to ensure consistency with retrieval
          const knowledgeBaseDocuments: Record<string, Record<string, unknown>> = {};
          documentsToAdd.forEach((doc, index) => {
            knowledgeBaseDocuments[fileUrls[index]] = buildKnowledgeBaseMetadata(doc);
          });
          
          // Update context state with the knowledge base document metadata
          const updatedKBDocs = {
            ...(contextState?.knowledgeBaseDocuments || {}),
          };
          Object.assign(updatedKBDocs, knowledgeBaseDocuments);
          
          onUpdateContextState({
            knowledgeBaseDocuments: updatedKBDocs,
            lastModified: new Date().toISOString(),
          });
          
          // Add documents as reference documents to the project
          await projectService.addMultipleReferenceDocuments(project.id, fileUrls);
          
          // Refresh context items
          const updatedContext = await projectService.getProjectContext(project.id);
          setContextItems(updatedContext);
          
          // Add selected documents to selectedSearchDocuments for display in context pane
          // Use documentsToAdd directly since they're already ExistingDocument format.
          // De-duplicate by canonical key so the same file cannot be added twice
          // even when ids differ (e.g. raw vs base64-encoded blob URLs).
          setSelectedSearchDocuments(prev => {
            const existingKeys = new Set(prev.map(getCanonicalDocumentKey));
            const existingNames = new Set(
              prev.map(getDocumentNameKey).filter((name) => name.length > 0),
            );
            const newDocs = documentsToAdd.filter(
              doc =>
                !existingKeys.has(getCanonicalDocumentKey(doc)) &&
                !existingNames.has(getDocumentNameKey(doc)),
            );
            return [...prev, ...newDocs];
          });
          
          // Clear selections in the browser after adding
          setSelectedDocuments([]);
        }
      } catch (error) {
        logger.error('Failed to handle document upload:', error);
      }
    })();
  }, [displayDocuments, selectedSearchDocuments, project.id, contextState, onUpdateContextState]);

  // Handle search from ContextBrowser
  const handleSearch = useCallback((query: string, filters?: SearchFilters, projectId?: string) => {
    if (query.trim() || searchService.isGuidelineBrowseRequest({ query, filters })) {
      performManualSearch(query, {
        sortBy: 'relevance',
        limit: 20,
        filters,
        project_id: projectId,
      });
    }
  }, [performManualSearch]);

  // Convert search results to ExistingDocument format for ContextBrowser
  const searchDocuments = searchResults.map(convertSearchResultToExistingDocument);

  // Simple: Display results immediately, then enhance with summaries one by one
  useEffect(() => {
    if (searchResults.length === 0) {
      setDocumentsWithSummaries([]);
      return;
    }

    // Step 1: Show results immediately
    const immediateResults = searchResults.map(convertSearchResultToExistingDocument);
    setDocumentsWithSummaries(immediateResults);

    // Step 2: Generate summaries one by one in background
    searchResults.forEach(async (result, index) => {
      try {
        const enhancedResult = await searchService.generateSingleSummary(result, 150);

        if (enhancedResult) {
          const enhancedDoc = convertSearchResultToExistingDocument(enhancedResult);

          // Update just this one document
          setDocumentsWithSummaries(prev =>
            prev.map(doc => doc.id === enhancedDoc.id ? enhancedDoc : doc),
          );

        }
      } catch (error) {
        logger.error(`Failed to generate summary for ${result.name}:`, error);
      }
    });
  }, [searchResults]);

  const isUploadedDocumentPreview = previewDocument?.category === 'Uploaded Document';

  // A document already added to the user context should not offer an
  // "Add to Context" action again. Match by canonical key so encoded and raw
  // identifiers for the same file are treated as the same document.
  const isPreviewDocumentInContext = useMemo(() => {
    if (!previewDocument) {
      return false;
    }
    const previewKey = getCanonicalDocumentKey(previewDocument);
    return selectedSearchDocuments.some(
      doc => getCanonicalDocumentKey(doc) === previewKey,
    );
  }, [previewDocument, selectedSearchDocuments]);

  // Hide the add/remove action when previewing an uploaded document or a
  // knowledge base document that is already part of the context.
  const hidePreviewSelectionAction =
    isUploadedDocumentPreview || isPreviewDocumentInContext;

  const hasRequiredBusinessRequirementsDocument = useMemo(
    () =>
      hasBusinessRequirementsDocument(contextItems) ||
      hasCompletedBusinessRequirementsUpload(uploadedFiles),
    [contextItems, uploadedFiles],
  );

  const handleStageNavigationClick = useCallback(
    (stageId: number, route: string): void => {
      if (stageId > 2 && !hasRequiredBusinessRequirementsDocument) {
        setActiveTab('upload');
        return;
      }

      navigate(route);
    },
    [hasRequiredBusinessRequirementsDocument, navigate],
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <StageNavigationBar
        currentStage={2}
        projectId={project.id}
        isBrownfield={isBrownfield}
        projectStatus={(() => {
          // Convert string status to enum
          const statusMap: Record<string, ProjectStatus> = {
            'context_upload': ProjectStatus.CONTEXT_UPLOAD,
            'context_review': ProjectStatus.CONTEXT_REVIEW,
            'adr': ProjectStatus.ADR,
            'generation': ProjectStatus.GENERATION,
            'bizzdesign_integration': ProjectStatus.BIZZDESIGN_INTEGRATION,
            'completed': ProjectStatus.COMPLETED,
          };
          return statusMap[project.status] ?? ProjectStatus.CONTEXT_UPLOAD;
        })()}
        onStageClick={handleStageNavigationClick}
      />

      <div className={`${STAGE_PAGE_WIDTH_CLASS} py-8`}>
        {/* Header */}
        <PageHeader
          title="Context Management"
          subtitle={`Upload and organize project context documents for ${project.title}`}
          icon={DocumentTextIcon}
          iconBgColor="#6B7280"
        >
          {/* Project Type Indicator */}
          <div className="mt-4 flex items-center gap-2">
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${
              (project.projectType || ProjectType.GREEN_FIELD) === ProjectType.BROWN_FIELD
                ? 'bg-orange-100 text-orange-800'
                : 'bg-green-100 text-green-800'
            }`}>
              {(project.projectType || ProjectType.GREEN_FIELD) === ProjectType.BROWN_FIELD ? 'Brown Field' : 'Green Field'} Project
            </span>
          </div>
        </PageHeader>

        <div className={STAGE_PAGE_CONTENT_WIDTH_CLASS}>
          {/* Context Sources */}
          <div className="space-y-6">
            {/* Brown Field Original Project Link
            {(project.projectType || ProjectType.GREEN_FIELD) === ProjectType.BROWN_FIELD && (
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <div className="flex items-center gap-2 mb-4">
                  <LinkIcon className="w-5 h-5 text-gray-400" />
                  <h3 className="text-lg font-semibold text-gray-900">Original Project Reference</h3>
                  <span className="text-red-500">*</span>
                </div>
                <div className="space-y-3">
                  <input
                    type="url"
                    placeholder="Enter original project URL or reference"
                    value={originalProjectLink}
                    onChange={(e) => handleOriginalProjectLinkChange(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0097a9] focus:border-transparent"
                  />
                  <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <ExclamationTriangleIcon className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-amber-700">
                      Brown field projects require a reference to the original system being enhanced or modernized.
                      This helps the AI understand existing constraints and integration requirements.
                    </p>
                  </div>
                </div>
              </div>
            )} */}

            {/* File Upload Section */}
            <div className="bg-white border border-gray-200 rounded-lg p-6">
              <div className="flex items-center gap-2 mb-4">
                <DocumentTextIcon className="w-5 h-5 text-gray-400" />
                <h3 className="text-lg font-semibold text-gray-900">Document Upload</h3>
              </div>

              {/* Tab Navigation */}
              <div className="flex mb-6 border-b border-gray-200">
                <button
                  onClick={() => setActiveTab('upload')}
                  className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === 'upload'
                      ? 'border-[#0097a9] text-[#0097a9]'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Upload New Files
                </button>
                <button
                  onClick={() => setActiveTab('wiki')}
                  className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === 'wiki'
                      ? 'border-[#0097a9] text-[#0097a9]'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <MagnifyingGlassIcon className="w-4 h-4" />
                    Search EAR Wiki for SAHLD / DSIs
                  </div>
                </button>
                <button
                  onClick={() => setActiveTab('guidelines')}
                  className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === 'guidelines'
                      ? 'border-[#0097a9] text-[#0097a9]'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <MagnifyingGlassIcon className="w-4 h-4" />
                    Search Guidelines
                  </div>
                </button>
                <button
                  onClick={() => setActiveTab('user_docs')}
                  className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === 'user_docs'
                      ? 'border-[#0097a9] text-[#0097a9]'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <MagnifyingGlassIcon className="w-4 h-4" />
                    Search User Documents
                  </div>
                </button>
              </div>

              {/* Tab Content */}
              {activeTab === 'upload' ? (
                <div className="space-y-4">
                  {/* Document Type Selection */}
                  <div>
                    <label
                      htmlFor="document-type-select"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Document Type <span className="text-red-500">*</span>
                    </label>
                    <select
                      id="document-type-select"
                      value={selectedDocumentType}
                      onChange={(e) => setSelectedDocumentType(e.target.value as ProjectContextUploadedDocumentType)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0097a9] focus:border-transparent"
                    >
                      <option value={ProjectContextUploadedDocumentType.BUSINESS_REQUIREMENTS_DOCUMENT}>
                        Business Requirements Document
                      </option>
                      <option value={ProjectContextUploadedDocumentType.GUIDELINES}>
                        Guidelines & Standards
                      </option>
                      <option value={ProjectContextUploadedDocumentType.SAHLD}>
                        SAHLD/DSI
                      </option>
                      <option value={ProjectContextUploadedDocumentType.C4_DIAGRAM}>
                        C4 Diagram
                      </option>
                      <option value={ProjectContextUploadedDocumentType.MEETING_TRANSCRIPT}>
                        Meeting Transcript
                      </option>
                      <option value={ProjectContextUploadedDocumentType.OTHER}>
                        Other Documentation
                      </option>
                    </select>
                    <p className="text-xs text-gray-500 mt-1">
                      Select the type of document you are uploading to help categorize the content
                    </p>
                  </div>

                  {/* Simple File Upload Area */}
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
                    <input
                      type="file"
                      multiple
                      accept={selectedDocumentType === ProjectContextUploadedDocumentType.MEETING_TRANSCRIPT ? '.docx' : '.txt, .md, .pptx, .pdf, .docx, .xlsx'}
                      onChange={(e) => {
                        const files = Array.from(e.target.files || []);
                        
                        // Validate meeting transcript file type
                        if (selectedDocumentType === ProjectContextUploadedDocumentType.MEETING_TRANSCRIPT) {
                          const invalidFiles = files.filter(f => !f.name.endsWith('.docx'));
                          if (invalidFiles.length > 0) {
                            alert('Meeting transcripts must be DOCX files (.docx)');
                            return;
                          }
                        }
                        
                        const uploadFiles = files.map(file => {
                          // For meeting transcripts, show the summary filename immediately
                          const isMeetingTranscript = selectedDocumentType === 
                            ProjectContextUploadedDocumentType.MEETING_TRANSCRIPT;
                          const displayName = isMeetingTranscript 
                            ? file.name.replace(/\.[^/.]+$/, '_summary.txt')
                            : file.name;
                          
                          return {
                            id: Math.random().toString(36).substr(2, 9),
                            file,
                            name: displayName,
                            size: file.size,
                            type: file.type,
                            status: DOC_STATUS_UPLOADING,
                          };
                        });
                        handleFilesUploaded(uploadFiles);
                        // Reset input
                        e.target.value = '';
                      }}
                      className="hidden"
                      id="file-upload"
                    />
                    <label
                      htmlFor="file-upload"
                      className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 bg-[#0097a9] text-white rounded-lg hover:bg-[#008191] transition-colors main-button"
                    >
                      <CloudArrowUpIcon className="w-5 h-5" />
                      Choose Files to Upload
                    </label>
                    <p className="text-sm text-gray-500 mt-2">
                      Upload your documents to provide context for architecture generation
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      Supported formats: TXT, MD, PPTX, PDF, DocX, XLSX
                    </p>
                    <p className="text-xs text-amber-600 mt-2 flex items-center justify-center gap-1">
                      ⚠️ Large documents may take several minutes to upload and index.
                    </p>
                  </div>
                </div>
              ) : (
                <div>
                  <ContextBrowser
                    documents={displayDocuments}
                    selectedDocuments={visibleSelectedDocuments}
                    onDocumentSelect={handleDocumentSelect}
                    onDocumentDeselect={handleDocumentDeselect}
                    onDocumentPreview={setPreviewDocument}
                    onDocumentAdd={handleAddSelectedDocuments}
                    onSearch={handleSearch}
                    isLoading={isSearchLoading}
                    searchPlaceholder={knowledgeBaseSearchPlaceholder}
                    isKnowledgeBase={true}
                    knowledgeBaseSource={activeKnowledgeBaseSource}
                  />
                  
                  {/* Show initial message when no search has been performed */}
                  {!hasSearched && !isSearchLoading && searchDocuments.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      <p className="text-sm">
                        Enter a search term above to find relevant documents from the knowledge base.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Context Documents Display */}
            <ContextDocuments
              uploadedFiles={uploadedFiles}
              selectedSearchDocuments={selectedSearchDocuments}
              onFileRemove={handleFileRemoved}
              onSearchDocumentRemove={handleSearchDocumentRemove}
              onDocumentPreview={(doc) => {
                // Preserve the original document fields (snippet/chunks/summary
                // for knowledge-base docs) and only set blobPath when this is a
                // genuinely uploaded file. Rebuilding from scratch previously
                // stripped the preview content for selected guideline docs.
                const convertedDoc: ExistingDocument = {
                  ...(doc as ExistingDocument),
                  blobPath:
                    (doc.metadata?.blob_path as string | undefined) ??
                    (doc as ExistingDocument).blobPath,
                };
                setPreviewDocument(convertedDoc);
              }}
            />

            {!hasRequiredBusinessRequirementsDocument && (
              <div
                className="rounded-lg border border-amber-200 bg-amber-50 p-4"
                role="alert"
              >
                <div className="flex items-start gap-3">
                  <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600" />
                  <div>
                    <h3 className="text-sm font-semibold text-amber-900">
                      {BUSINESS_REQUIREMENTS_DOCUMENT_REQUIRED_TITLE}
                    </h3>
                    <p className="mt-1 text-sm text-amber-800">
                      {BUSINESS_REQUIREMENTS_DOCUMENT_REQUIRED_MESSAGE}
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-6">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <PencilSquareIcon className="w-5 h-5" />
                    Project Instruction &amp; Solution Ideation
                  </h3>
                  <p className="mt-1 text-sm text-gray-600">
                    Add project instructions and solution context that will be
                    saved with this project.
                  </p>
                </div>
                <button
                  onClick={handleGenerateSummaries}
                  disabled={isGeneratingSummaries}
                  className="bg-[#336094] text-white px-4 py-2 rounded-md hover:bg-[#2a4d7a] transition-colors text-sm main-button disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isGeneratingSummaries ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                      <span>Generating...</span>
                    </>
                  ) : (
                    <span>Generate Ideas</span>
                  )}
                </button>
              </div>

              <div className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-4">
                <div className="bg-white rounded-lg p-6 border border-gray-200 hover:shadow-md transition-shadow">
                  <div className="flex items-center justify-between mb-3 gap-3">
                    <h4 className="font-semibold text-gray-900">
                      Project Instructions
                    </h4>
                    {projectInstructions.trim() && (
                      isSavingInstructions ? (
                        <div
                          className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-400"
                          title="Saving..."
                        />
                      ) : (
                        <CheckCircleIcon
                          className="w-5 h-5 text-[#008557]"
                          title="Saved"
                        />
                      )
                    )}
                  </div>
                  <textarea
                    value={projectInstructions}
                    onChange={(event) => handleInstructionsChange(event.target.value)}
                    placeholder="Provide detailed instructions on how to use the uploaded context..."
                    rows={6}
                    className="w-full px-0 py-0 border-none focus:ring-0 focus:outline-none resize-none text-sm text-gray-600 placeholder-gray-400"
                  />
                  <div className="mt-3 flex items-center justify-end text-xs text-gray-500">
                    {instructionsUpdatedAt && (
                      <span>
                        Last updated: {formatTimestampToLocal(instructionsUpdatedAt)}
                      </span>
                    )}
                  </div>
                </div>

                {SOLUTION_IDEATION_FIELDS.map(({ key, title, placeholder }) => (
                  <div key={key} className="bg-white rounded-lg p-6 border border-gray-200 hover:shadow-md transition-shadow">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-semibold text-gray-900">{title}</h4>
                      {solutionContext[key].trim() && (
                        isSavingSolutionContext ? (
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-400" title="Saving..." />
                        ) : savedSolutionContextFields.has(key) ? (
                          <CheckCircleIcon className="w-5 h-5 text-[#008557]" title="Saved" />
                        ) : (
                          <CheckCircleIcon className="w-5 h-5 text-gray-400" title="Not saved yet" />
                        )
                      )}
                    </div>
                    <textarea
                      value={solutionContext[key]}
                      onChange={(event) => handleSolutionContextChange(key, event.target.value)}
                      placeholder={placeholder}
                      rows={6}
                      className="w-full px-0 py-0 border-none focus:ring-0 focus:outline-none resize-none text-sm text-gray-600 placeholder-gray-400"
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Navigation */}
        <BottomNavigationBar
          backAction={{
            label: BACK_BUTTON_LABEL,
            onClick: () => onNavigateToStage(1),
          }}
          primaryActions={[
            ...(isBrownfield
              ? [
                {
                  label: CONTINUE_BUTTON_LABEL,
                  onClick: () =>
                    navigate(
                      `/project/${project.id}/brownfield-conversion`,
                    ),
                  disabled: !hasRequiredBusinessRequirementsDocument,
                  tooltip: BUSINESS_REQUIREMENTS_DOCUMENT_REQUIRED_TOOLTIP,
                  variant: 'primary' as const,
                },
              ]
              : [
                {
                  label: CONTINUE_BUTTON_LABEL,
                  onClick: () => onNavigateToStage(3),
                  disabled: !hasRequiredBusinessRequirementsDocument,
                  tooltip: BUSINESS_REQUIREMENTS_DOCUMENT_REQUIRED_TOOLTIP,
                  variant: 'primary' as const,
                },
              ]),
          ]}
        />
      </div>

      {/* Document Preview Modal */}
      <DocumentPreview
        document={previewDocument}
        isOpen={!!previewDocument}
        onClose={() => setPreviewDocument(null)}
        onSelect={hidePreviewSelectionAction ? undefined : handleDocumentSelect}
        onDeselect={hidePreviewSelectionAction ? undefined : handleDocumentDeselect}
        isSelected={previewDocument ? selectedDocuments.includes(previewDocument.id) : false}
      />
      
      {/* Upload Progress Modal */}
      <UploadModal
        isOpen={isUploadModalOpen}
        filesCount={uploadProgress.total}
        completedCount={uploadProgress.completed}
        failedCount={uploadProgress.failed}
      />
    </div>
  );
};
